// WAV encoder for AudioBuffers. Supports 16/24-bit PCM and 32-bit float so we
// can keep professional fidelity (24-bit downloads; 32-bit float = exact,
// lossless round-trip of the in-memory float buffer for storage/transfer).
// Headers follow the RIFF/WAVE spec exactly (float files carry cbSize + a
// `fact` chunk) so every DAW/NLE reads them, and long renders can be written
// in pieces: wavHeader() once, then pcmBytes() per chunk.
export type BitDepth = 16 | 24 | 32;

const RIFF_MAX = 0xffffffff;

/** RIFF/WAVE header for `frames` sample frames. */
export function wavHeader(frames: number, numCh: number, rate: number, bitDepth: BitDepth): Uint8Array {
  const isFloat = bitDepth === 32;
  const blockAlign = numCh * (bitDepth / 8);
  const dataSize = frames * blockAlign;
  const fmtSize = isFloat ? 18 : 16;
  const headerSize = 12 + 8 + fmtSize + (isFloat ? 12 : 0) + 8;
  if (headerSize - 8 + dataSize > RIFF_MAX) throw new Error("This mix is too long for a single WAV file (4 GB limit) — export M4A, or a shorter range.");
  const dv = new DataView(new ArrayBuffer(headerSize));
  const str = (off: number, s: string) => { for (let i = 0; i < s.length; i++) dv.setUint8(off + i, s.charCodeAt(i)); };
  str(0, "RIFF"); dv.setUint32(4, headerSize - 8 + dataSize, true); str(8, "WAVE");
  str(12, "fmt "); dv.setUint32(16, fmtSize, true);
  dv.setUint16(20, isFloat ? 3 : 1, true); // 1 = PCM, 3 = IEEE float
  dv.setUint16(22, numCh, true);
  dv.setUint32(24, rate, true);
  dv.setUint32(28, rate * blockAlign, true);
  dv.setUint16(32, blockAlign, true);
  dv.setUint16(34, bitDepth, true);
  let o = 36;
  if (isFloat) { dv.setUint16(o, 0, true); o += 2; str(o, "fact"); dv.setUint32(o + 4, 4, true); dv.setUint32(o + 8, frames, true); o += 12; }
  str(o, "data"); dv.setUint32(o + 4, dataSize, true);
  return new Uint8Array(dv.buffer);
}

/** Interleaved sample bytes for one buffer (no header). 16-bit gets TPDF dither. */
export function pcmBytes(buffer: AudioBuffer, bitDepth: BitDepth, numCh = buffer.numberOfChannels): Uint8Array {
  const len = buffer.length;
  const bps = bitDepth / 8;
  const out = new Uint8Array(len * numCh * bps);
  const dv = new DataView(out.buffer);
  const ch: Float32Array[] = [];
  for (let c = 0; c < numCh; c++) ch.push(buffer.getChannelData(Math.min(c, buffer.numberOfChannels - 1)));
  let off = 0;
  for (let i = 0; i < len; i++) {
    for (let c = 0; c < numCh; c++) {
      const x = ch[c][i];
      if (bitDepth === 32) { dv.setFloat32(off, x, true); off += 4; continue; }
      if (bitDepth === 24) {
        const s = Math.max(-1, Math.min(1, x));
        const v = Math.round(s < 0 ? s * 0x800000 : s * 0x7fffff);
        out[off] = v & 0xff; out[off + 1] = (v >> 8) & 0xff; out[off + 2] = (v >> 16) & 0xff;
        off += 3; continue;
      }
      // 16-bit: triangular dither (±1 LSB) avoids quantization distortion on fades
      const d = (Math.random() - Math.random()) / 0x8000;
      const s = Math.max(-1, Math.min(1, x + d));
      dv.setInt16(off, Math.round(s < 0 ? s * 0x8000 : s * 0x7fff), true); off += 2;
    }
  }
  return out;
}

export function wavBytes(buffer: AudioBuffer, bitDepth: BitDepth = 24): Uint8Array {
  const head = wavHeader(buffer.length, buffer.numberOfChannels, buffer.sampleRate, bitDepth);
  const body = pcmBytes(buffer, bitDepth);
  const out = new Uint8Array(head.length + body.length);
  out.set(head, 0); out.set(body, head.length);
  return out;
}

export function encodeWav(buffer: AudioBuffer, bitDepth: BitDepth = 24): Blob {
  return new Blob([wavBytes(buffer, bitDepth) as BlobPart], { type: "audio/wav" });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000); // big files need time to start downloading
}
